/**
 * Step to create account for Clever Library users.
 */
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingMedium,
    LabelLarge,
} from "@khanacademy/wonder-blocks-typography";

import ForgotPasswordMutation from "./forgot-password-mutation.tsx";

interface Props {
    /**
     * Optional identifier to use for the dialog title.
     *
     * Usefult for announcing the dialog to Screen Readers, as it will be used
     * as the `aria-labelledby` attribute of the dialog.
     */
    titleId?: string;
}

export default function ForgotPasswordStep({titleId}: Props) {
    const [state, setState] = React.useState({
        email: "",
        submitting: false,
    });

    const handleChange = (email: string) => {
        setState((prevState) => ({
            ...prevState,
            email,
        }));
    };

    const {email} = state;

    return (
        <ForgotPasswordMutation>
            {(submitForgotPassword, {data, isLoading, isError}) => {
                let errorMessage;

                if (isError) {
                    errorMessage = t`Sorry, we saw an unexpected error! Please try again later.`;
                }

                if (data && data.error) {
                    const error = data.error.code;
                    if (error === "NOT_FOUND") {
                        errorMessage = t`Sorry, we can't find a Khan Academy account connected to that email.`;
                    } else {
                        errorMessage = t`Sorry, we saw an unexpected error! Please try again later.`;
                    }
                } else if (data && !data.error) {
                    return (
                        <View>
                            <Strut size={spacing.medium_16} />
                            <HeadingMedium id={titleId}>
                                {t`Reset your password`}
                            </HeadingMedium>
                            <Strut size={spacing.small_12} />
                            <Body>
                                <Trans>
                                    We've sent you a message at {email}
                                </Trans>
                            </Body>
                            <Strut size={spacing.medium_16} />
                            <Body>
                                {t`Follow the link in that message to reset your password.`}
                            </Body>
                        </View>
                    );
                }

                return (
                    <View>
                        <Strut size={spacing.medium_16} />
                        <HeadingMedium id={titleId}>
                            {t`Reset your password`}
                        </HeadingMedium>
                        <Strut size={spacing.small_12} />
                        <Body>
                            {t`Enter the email associated with your Khan Academy account and we'll email you a link to reset your password.`}
                        </Body>
                        <Strut size={spacing.large_24} />
                        <LabelLarge tag="label">{t`Email`}</LabelLarge>
                        <Strut size={spacing.xSmall_8} />
                        <TextField
                            type="email"
                            placeholder={t`email@example.com`}
                            value={email || ""}
                            onChange={handleChange}
                        />
                        {errorMessage ? (
                            <View>
                                <Strut size={spacing.medium_16} />
                                <Body style={styles.error}>{errorMessage}</Body>
                                <Strut size={spacing.medium_16} />
                            </View>
                        ) : (
                            <Strut size={spacing.large_24} />
                        )}
                        <Button
                            onClick={() => {
                                submitForgotPassword(state.email);
                            }}
                            disabled={!email || isLoading}
                            spinner={isLoading}
                        >
                            {t`Email a reset link`}
                        </Button>
                    </View>
                );
            }}
        </ForgotPasswordMutation>
    );
}

const styles = StyleSheet.create({
    error: {
        color: semanticColor.core.foreground.critical.subtle,
    },
});
